/**
 * country-flag-emoji
 * For Media Devoted | Created By Ciro Autiero
 *
 * Converts a country name or ISO2 code to a flag emoji.
 * Handles "every" ISO-3166 country name + common aliases.
 * Falls back to '🌍' when unknown.
 */

const COUNTRY_ISO2_DATA = `
AD|Andorra
AE|United Arab Emirates
AF|Afghanistan
AG|Antigua and Barbuda
AI|Anguilla
AL|Albania
AM|Armenia
AO|Angola
AQ|Antarctica
AR|Argentina
AS|American Samoa
AT|Austria
AU|Australia
AW|Aruba
AX|Åland Islands
AZ|Azerbaijan
BA|Bosnia and Herzegovina
BB|Barbados
BD|Bangladesh
BE|Belgium
BF|Burkina Faso
BG|Bulgaria
BH|Bahrain
BI|Burundi
BJ|Benin
BL|Saint Barthélemy
BM|Bermuda
BN|Brunei
BO|Bolivia
BQ|Bonaire, Sint Eustatius and Saba
BR|Brazil
BS|Bahamas
BT|Bhutan
BV|Bouvet Island
BW|Botswana
BY|Belarus
BZ|Belize
CA|Canada
CC|Cocos (Keeling) Islands
CD|Congo (DRC)
CF|Central African Republic
CG|Congo
CH|Switzerland
CI|Ivory Coast
CK|Cook Islands
CL|Chile
CM|Cameroon
CN|China
CO|Colombia
CR|Costa Rica
CU|Cuba
CV|Cabo Verde
CW|Curaçao
CX|Christmas Island
CY|Cyprus
CZ|Czech Republic
DE|Germany
DJ|Djibouti
DK|Denmark
DM|Dominica
DO|Dominican Republic
DZ|Algeria
EC|Ecuador
EE|Estonia
EG|Egypt
EH|Western Sahara
ER|Eritrea
ES|Spain
ET|Ethiopia
FI|Finland
FJ|Fiji
FK|Falkland Islands
FM|Federated States of Micronesia
FO|Faroe Islands
FR|France
GA|Gabon
GB|United Kingdom
GD|Grenada
GE|Georgia
GF|French Guiana
GG|Guernsey
GH|Ghana
GI|Gibraltar
GL|Greenland
GM|Gambia
GN|Guinea
GP|Guadeloupe
GQ|Equatorial Guinea
GR|Greece
GS|South Georgia and the South Sandwich Islands
GT|Guatemala
GU|Guam
GW|Guinea-Bissau
GY|Guyana
HK|Hong Kong
HM|Heard Island and McDonald Islands
HN|Honduras
HR|Croatia
HT|Haiti
HU|Hungary
ID|Indonesia
IE|Ireland
IL|Israel
IM|Isle of Man
IN|India
IO|British Indian Ocean Territory
IQ|Iraq
IR|Iran
IS|Iceland
IT|Italy
JE|Jersey
JM|Jamaica
JO|Jordan
JP|Japan
KE|Kenya
KG|Kyrgyzstan
KH|Cambodia
KI|Kiribati
KM|Comoros
KN|Saint Kitts and Nevis
KP|North Korea
KR|South Korea
KW|Kuwait
KY|Cayman Islands
KZ|Kazakhstan
LA|Laos
LB|Lebanon
LC|Saint Lucia
LI|Liechtenstein
LK|Sri Lanka
LR|Liberia
LS|Lesotho
LT|Lithuania
LU|Luxembourg
LV|Latvia
LY|Libya
MA|Morocco
MC|Monaco
MD|Moldova
ME|Montenegro
MF|Saint Martin
MG|Madagascar
MH|Marshall Islands
MK|North Macedonia
ML|Mali
MM|Myanmar
MN|Mongolia
MO|Macao
MP|Northern Mariana Islands
MQ|Martinique
MR|Mauritania
MS|Montserrat
MT|Malta
MU|Mauritius
MV|Maldives
MW|Malawi
MX|Mexico
MY|Malaysia
MZ|Mozambique
NA|Namibia
NC|New Caledonia
NE|Niger
NF|Norfolk Island
NG|Nigeria
NI|Nicaragua
NL|Netherlands
NO|Norway
NP|Nepal
NR|Nauru
NU|Niue
NZ|New Zealand
OM|Oman
PA|Panama
PE|Peru
PF|French Polynesia
PG|Papua New Guinea
PH|Philippines
PK|Pakistan
PL|Poland
PM|Saint Pierre and Miquelon
PN|Pitcairn Islands
PR|Puerto Rico
PS|Palestine
PT|Portugal
PW|Palau
PY|Paraguay
QA|Qatar
RE|Réunion
RO|Romania
RS|Serbia
RU|Russia
RW|Rwanda
SA|Saudi Arabia
SB|Solomon Islands
SC|Seychelles
SD|Sudan
SE|Sweden
SG|Singapore
SH|St. Helena
SI|Slovenia
SJ|Svalbard and Jan Mayen
SK|Slovakia
SL|Sierra Leone
SM|San Marino
SN|Senegal
SO|Somalia
SR|Suriname
SS|South Sudan
ST|Sao Tome and Principe
SV|El Salvador
SX|Sint Maarten
SY|Syria
SZ|Eswatini
TC|Turks and Caicos Islands
TD|Chad
TF|French Southern Territories
TG|Togo
TH|Thailand
TJ|Tajikistan
TK|Tokelau
TL|Timor-Leste
TM|Turkmenistan
TN|Tunisia
TO|Tonga
TR|Turkey
TT|Trinidad and Tobago
TV|Tuvalu
TW|Taiwan
TZ|Tanzania
UA|Ukraine
UG|Uganda
UM|United States Minor Outlying Islands
US|United States
UY|Uruguay
UZ|Uzbekistan
VA|Vatican City
VC|Saint Vincent and the Grenadines
VE|Venezuela
VG|British Virgin Islands
VI|U.S. Virgin Islands
VN|Vietnam
VU|Vanuatu
WF|Wallis and Futuna
WS|Samoa
YE|Yemen
YT|Mayotte
ZA|South Africa
ZM|Zambia
ZW|Zimbabwe
`.trim();

function normalizeCountryKey(input: string): string {
  return (
    (input || '')
      .trim()
      .toLowerCase()
      // remove accents/diacritics
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // unify punctuation & whitespace
      .replace(/&/g, 'and')
      .replace(/[()'",.]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

const ALIASES: Record<string, string> = {
  // common abbreviations / alternate names
  uae: 'AE',
  uk: 'GB',
  'united kingdom': 'GB',
  'great britain': 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  'northern ireland': 'GB',

  usa: 'US',
  'united states': 'US',
  'united states of america': 'US',

  russia: 'RU',
  'south korea': 'KR',
  'north korea': 'KP',
  bolivia: 'BO',
  venezuela: 'VE',
  iran: 'IR',
  syria: 'SY',
  vietnam: 'VN',
  laos: 'LA',
  myanmar: 'MM',
  burma: 'MM',
  'czech republic': 'CZ',
  czech: 'CZ',
  // Pre-rename stored spelling (see shared/src/country/country-key-to-iso2.ts).
  czechia: 'CZ',
  turkey: 'TR',
  // Pre-rename stored spelling; the diacritic is stripped before lookup.
  turkiye: 'TR',
  'cape verde': 'CV',
  'ivory coast': 'CI',
  palestine: 'PS',
  macedonia: 'MK',
  taiwan: 'TW',
  'hong kong': 'HK',
  macau: 'MO',
  // Pre-rename ISO 3166 official names. The tables now carry the common
  // English name the trackers use; these keep older stored values and raw
  // tracker payloads resolving to the same country.
  'brunei darussalam': 'BN',
  'bolivia plurinational state of': 'BO',
  'congo the democratic republic of the': 'CD',
  'cote d ivoire': 'CI',
  'falkland islands malvinas': 'FK',
  'micronesia federated states of': 'FM',
  'iran islamic republic of': 'IR',
  'korea democratic people s republic of': 'KP',
  'korea republic of': 'KR',
  'lao people s democratic republic': 'LA',
  'moldova republic of': 'MD',
  'saint martin french part': 'MF',
  pitcairn: 'PN',
  'palestine state of': 'PS',
  'russian federation': 'RU',
  'saint helena ascension and tristan da cunha': 'SH',
  'sint maarten dutch part': 'SX',
  'syrian arab republic': 'SY',
  'taiwan province of china': 'TW',
  'tanzania united republic of': 'TZ',
  // 'united states of america' is already aliased above.
  'holy see': 'VA',
  'venezuela bolivarian republic of': 'VE',
  'virgin islands british': 'VG',
  'virgin islands u s': 'VI',
  'viet nam': 'VN',
};

const NAME_TO_ISO2 = (() => {
  const map = new Map<string, string>();

  for (const line of COUNTRY_ISO2_DATA.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const [iso2Raw, nameRaw] = trimmed.split('|');
    const iso2 = (iso2Raw || '').trim().toUpperCase();
    const name = (nameRaw || '').trim();
    if (!/^[A-Z]{2}$/.test(iso2) || !name) continue;
    map.set(normalizeCountryKey(name), iso2);
  }

  for (const [alias, iso2] of Object.entries(ALIASES)) {
    map.set(normalizeCountryKey(alias), iso2.toUpperCase());
  }

  return map;
})();

function iso2ToFlagEmoji(iso2: string): string {
  const code = iso2.toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return '🌍';

  const A = 0x1f1e6; // Regional Indicator Symbol Letter A
  const first = code.charCodeAt(0) - 65 + A;
  const second = code.charCodeAt(1) - 65 + A;
  return String.fromCodePoint(first, second);
}

/**
 * For countryKey variants like belgium_fr, belgium_nl, use the base part for flag lookup.
 */
function extractBaseCountryKey(input: string): string {
  const trimmed = (input || '').trim().toLowerCase();
  if (!trimmed) return '';
  const idx = trimmed.indexOf('_');
  return idx >= 0 ? trimmed.slice(0, idx) : trimmed;
}

/**
 * The ISO2 of a country key or name, or undefined: the full key, then
 * underscores as spaces (new_zealand), then the base of a key variant
 * (belgium_fr -> belgium), then a display name without its language variant
 * (20/10/2026 — "Belgium (FR)" / "Belgium NL" showed 🌍 on the Automation
 * index, which passes the name, not the key).
 */
function resolveIso2(raw: string): string | undefined {
  const key = normalizeCountryKey(raw);
  const hit = NAME_TO_ISO2.get(key);
  if (hit) return hit;
  if (key.includes('_')) {
    const spaced = NAME_TO_ISO2.get(key.replace(/_/g, ' '));
    if (spaced) return spaced;
    const base = NAME_TO_ISO2.get(normalizeCountryKey(extractBaseCountryKey(raw)));
    if (base) return base;
  }
  const variant = /^(.+)\s[a-z]{2}$/.exec(key);
  return variant ? NAME_TO_ISO2.get(variant[1]) : undefined;
}

export function countryToFlagEmoji(country: string | undefined | null): string {
  const raw = (country || '').trim();
  if (!raw) return '🌍';

  // Already a flag emoji (two regional-indicator symbols)
  if (/^[\u{1F1E6}-\u{1F1FF}]{2}$/u.test(raw)) {
    return raw;
  }

  // If already ISO2
  if (/^[A-Za-z]{2}$/.test(raw)) {
    return iso2ToFlagEmoji(raw);
  }

  const iso2 = resolveIso2(raw);
  return iso2 ? iso2ToFlagEmoji(iso2) : '🌍';
}

const COUNTRY_SPLIT_RE = /[-–—•|/,>()[\]]+/;

function titleCaseWords(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Best-effort country extraction from free text (e.g. a campaign name like
 * "CASINO SMS - Australia - MAINFLOW"). Splits on common separators and returns
 * the first segment that maps to a known country, title-cased. `null` if none.
 *
 * Useful when a provider (e.g. Binom list endpoint) does not expose a structured
 * country, but the name follows the "<brand> - <Country> - …" convention.
 */
export function inferCountryNameFromText(
  text: string | undefined | null,
): string | null {
  const raw = (text || '').trim();
  if (!raw) return null;
  for (const segment of raw.split(COUNTRY_SPLIT_RE)) {
    const key = normalizeCountryKey(segment);
    if (key && NAME_TO_ISO2.has(key)) return titleCaseWords(segment);
  }
  return null;
}

/**
 * Get ISO 3166-1 alpha-2 code for a country key (e.g. "australia" -> "AU").
 * Returns uppercase 2-letter code or the original key if unknown.
 */
export function countryKeyToIso2(country: string | undefined | null): string {
  const raw = (country || '').trim();
  if (!raw) return '';

  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();

  return resolveIso2(raw) ?? raw.slice(0, 2).toUpperCase();
}

/** Sorted ISO-3166 country names — for country dropdowns. */
export const COUNTRY_NAMES: string[] = COUNTRY_ISO2_DATA.split('\n')
  .map((line) => line.split('|')[1]?.trim())
  .filter((name): name is string => Boolean(name))
  .sort((a, b) => a.localeCompare(b));
